import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbAlert, NgbCalendar, NgbDate, NgbDatepickerConfig, NgbDatepickerModule, NgbDateStruct, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from '@shell/message.service';
import { SharedModule } from "@shared/shared.module";
import { NgIf } from "@angular/common";
import { SaldoCliente } from "@shared/models/controlCredito/SaldoCliente";
import { UsuarioService } from "@account/usuario.service";
import { UsuarioDTO } from "@shared/models/notificaciones";
import { EstadoSolicitudCrediticia } from "@shared/enums/EstadoSolicitudCrediticia";
import { SolicitudCrediticiaDTO } from "@shared/models/controlCredito/SolicitudCrediticiaDTO";
import { SolicitudCreditoDTO } from "@shared/models/controlCredito/SaldoCliente";
import { ControlCreditoService } from "@controlCredito/control-credito.service";
import { ErrorModel } from "@shared/models/ErrorModel";

@Component({
  selector: 'app-modal-solicitar-credito',
  standalone: true,
  imports: [ReactiveFormsModule, NgbDatepickerModule, NgbTooltipModule, SharedModule, NgIf, NgbAlert],
  templateUrl: './modal-solicitar-credito.component.html',
  styleUrl: './modal-solicitar-credito.component.scss'
})
export class ModalSolicitarCreditoComponent implements OnInit{
  form!: FormGroup;
  guardando: boolean = false;
  @Input() cliente!: SaldoCliente;
  envioSolicitud: SolicitudCrediticiaDTO;

  constructor(
      private messageService: MessageService,
      public calendar: NgbCalendar,
      private controlCreditoService: ControlCreditoService,
      private datepickerConfig: NgbDatepickerConfig,
      private activeModal: NgbActiveModal,
      private usuarioService: UsuarioService
    ) { }

  ngOnInit(): void {
    this.crearForm()
    const today = this.calendar.getToday();
    this.datepickerConfig.minDate = today;

    this.form.patchValue({
      fechaDesdeFormControlExcedido: today,
      fechaHastaFormControlExcedido: today,
      fechaDesdeFormControlVencido: today,
      fechaHastaFormControlVencido: today
    });

  }

  crearForm(): void {
    this.form = new FormGroup({
      excedidoAutorizado: new FormControl(null, Validators.min(1)),
      vencidoAutorizado: new FormControl(null, Validators.min(1)),
      fechaDesdeFormControlExcedido: new FormControl(null, Validators.required),
      fechaHastaFormControlExcedido: new FormControl(null, Validators.required),
      fechaDesdeFormControlVencido: new FormControl(null, Validators.required),
      fechaHastaFormControlVencido: new FormControl(null, Validators.required),
      observacionesFormControl: new FormControl(null, Validators.required)
    });

    this.form.setValidators([
      this.validarRangoFecha('fechaDesdeFormControlExcedido', 'fechaHastaFormControlExcedido'),
      this.validarRangoFecha('fechaDesdeFormControlVencido', 'fechaHastaFormControlVencido')
    ]);
  }

  validarRangoFecha(fechaDesde: string, fechaHasta: string) {
    return (formGroup: FormGroup) => {
      const desde = formGroup.get(fechaDesde)?.value as NgbDateStruct;
      const hasta = formGroup.get(fechaHasta)?.value as NgbDateStruct;

      if (desde && hasta && NgbDate.from(desde).after(NgbDate.from(hasta))) {
        formGroup.get(fechaHasta)?.setErrors({ rangoInvalido: true });
        return { rangoInvalido: true };
      } else {
        formGroup.get(fechaHasta)?.setErrors(null);
        return null;
      }
    };
  }

  formatDate(date: NgbDateStruct | null, esFechaHasta: boolean): string {
    if (!date) return '';
    const year = date.year;
    const month = String(date.month).padStart(2, '0'); // Asegura dos dígitos (ej: 05)
    const day = String(date.day).padStart(2, '0');
    let hour = 0;
    let minute = 0;
    if (esFechaHasta) {
      hour = 23;
      minute = 59;
    }

    // Formatear hora y minutos con ceros iniciales
    const hourStr = String(hour).padStart(2, '0');
    const minuteStr = String(minute).padStart(2, '0');

    return `${year}-${month}-${day}T${hourStr}:${minuteStr}`;
  }

  onGuardar(){
    if(this.form.valid){
      this.guardando = true;

      if (!this.cliente.solicitudesCrediticias) {
        this.cliente.solicitudesCrediticias = [];
      }
      const fechaDesdeExcedido = this.formatDate(this.fechaDesdeFormControlExcedido?.value, false);
      const fechaHastaExcedido = this.formatDate(this.fechaHastaFormControlExcedido?.value, true);
      const fechaDesdeVencido = this.formatDate(this.fechaDesdeFormControlVencido?.value, false);
      const fechaHastaVencido = this.formatDate(this.fechaHastaFormControlVencido?.value, true);

      const nuevaSolicitud: SolicitudCreditoDTO = {
        id: undefined,
        excedido: this.excedidoAutorizado.value ? {
          monto: this.excedidoAutorizado.value,
          fechaDesde: fechaDesdeExcedido,
          fechaHasta: fechaHastaExcedido
        } : undefined,

        vencido: this.vencidoAutorizado.value ? {
          monto: this.vencidoAutorizado.value,
          fechaDesde: fechaDesdeVencido,
          fechaHasta: fechaHastaVencido
        } : undefined,

        observaciones: this.observacionesFormControl.value,
        usuarioSolicitante: this.obtenerUsuario(),
        estado: EstadoSolicitudCrediticia.Pendiente
      };

      this.envioSolicitud = {
        clienteId: this.cliente.clienteId,
        solicitudCrediticia: nuevaSolicitud
      };

      //Llamada al service
      this.controlCreditoService.postSolicitarCredito(this.envioSolicitud).subscribe(solicitud => {
        this.cliente.solicitudesCrediticias.push(solicitud.solicitudCrediticia);
        this.messageService.showSuccessMessage("Solicitud exitosa");
        this.guardando = false;
        this.activeModal.close();
      }, (err: ErrorModel)=> {
        this.messageService.showErrorMessage('Error en la solicitud de saldo. ' + err.description);
        this.guardando = false;
      });
    }
  }

  onCancelar() {
    this.activeModal.dismiss();
  }

  obtenerUsuario() : UsuarioDTO {
    const usuario = {
      id: this.usuarioService.getIdUsuario(),
      nombresCompletos: this.usuarioService.getNombresCompletosUsuario(),
      dni: this.usuarioService.getDniUsuario(),
      userName: this.usuarioService.getUsernameByName(),
      email: this.usuarioService.getEmailUsuario()
    }
    return usuario;
  }

  mensajeVacio(): boolean{
    return !(this.observacionesFormControl?.value?.trim()?.length > 0);
  }

  get excedidoAutorizado() {
    return this.form.get('excedidoAutorizado')
  }

  get vencidoAutorizado() {
    return this.form.get('vencidoAutorizado')
  }

  get fechaDesdeFormControlExcedido() {
    return this.form.get('fechaDesdeFormControlExcedido')
  }

  get fechaDesdeFormControlVencido() {
    return this.form.get('fechaDesdeFormControlVencido')
  }

  get fechaHastaFormControlExcedido() {
    return this.form.get('fechaHastaFormControlExcedido')
  }

  get fechaHastaFormControlVencido() {
    return this.form.get('fechaHastaFormControlVencido')
  }

  get observacionesFormControl() {
    return this.form.get('observacionesFormControl')
  }
}
